import { SelectField } from "@/components/fields";
import type { GroupRRSPPlan } from "@/generated/core/v1/person";
import { money } from "@/plan-logic/lib/format";
import {
  CONTRIBUTION_RATES,
  contributionRateValues,
  MATCH_CAPS,
  MATCH_RATES,
} from "@/plan-logic/questionnaire/draft";

const pct = (r: number) => `${+(r * 100).toFixed(2)}%`;

/** The rate choices, plus the stored one when it isn't among them (a check-in can confirm 4.32%). */
const contributionOptions = (own: number): [number, string][] =>
  contributionRateValues(own).map((v) => [
    v,
    CONTRIBUTION_RATES.find(([c]) => c === v)?.[1] ?? `${pct(v)} of salary`,
  ]);

/** What the offer means in dollars on this salary, at the employee's own rate. */
function offerHint(offer: GroupRRSPPlan | undefined, salary: number | undefined): string {
  if (!offer || !salary) return "Check your benefits package; leave as No match if unsure.";
  const {
    employerMatchingRate: match,
    employerMatchCapRate: cap,
    employeeContributionRate: own,
  } = offer;
  const full = salary * cap * match;
  if (!own)
    return `You put in nothing now. At ${pct(cap)} you'd put in ${money(salary * cap)} and your employer would add ${money(full)}.`;
  if (own < cap)
    return `Today you put in ${money(salary * own)} a year and your employer adds ${money(salary * own * match)}. At ${pct(cap)} they'd add ${money(full)}.`;
  return `You put in ${money(salary * own)} a year and your employer adds the full ${money(full)}.`;
}

/** An employer's group RRSP offer and the employee's own contribution, with what they mean in dollars. */
export function GroupRrspOfferField({
  offer,
  salary,
  onChange,
  testId,
  accounts,
}: {
  offer: GroupRRSPPlan | undefined;
  salary: number | undefined;
  onChange: (offer: GroupRRSPPlan | undefined) => void;
  /** The rate select's test id; the cap's is `${testId}-cap`. */
  testId: string;
  /** The person's group RRSP accounts, given only when which one the match goes into is ambiguous. */
  accounts?: [string, string][];
}) {
  return (
    <div style={{ marginTop: 12 }}>
      <div className="two">
        <SelectField
          label="Employer RRSP matching"
          testId={testId}
          value={offer?.employerMatchingRate ?? 0}
          options={MATCH_RATES}
          onChange={(rate) =>
            onChange(
              rate
                ? {
                    employerMatchCapRate: 0.05,
                    employeeContributionRate: 0,
                    ...offer,
                    employerMatchingRate: rate,
                  }
                : undefined,
            )
          }
          style={{ margin: 0 }}
        />
        {offer && (
          <SelectField
            label="Matched up to"
            testId={`${testId}-cap`}
            value={offer.employerMatchCapRate}
            options={MATCH_CAPS}
            onChange={(cap) => onChange({ ...offer, employerMatchCapRate: cap })}
            style={{ margin: 0 }}
          />
        )}
      </div>
      {offer && (
        <div className="two" style={{ marginTop: 12 }}>
          <SelectField
            label="Your contribution"
            testId={`${testId}-own`}
            value={offer.employeeContributionRate}
            options={contributionOptions(offer.employeeContributionRate)}
            onChange={(rate) => onChange({ ...offer, employeeContributionRate: rate })}
            style={{ margin: 0 }}
          />
          {accounts && (
            <SelectField
              label="Goes into"
              testId={`${testId}-account`}
              value={offer.groupRrspUuid ?? ""}
              options={[...accounts, ["", "A new account"]]}
              onChange={(uuid) => onChange({ ...offer, groupRrspUuid: uuid || undefined })}
              style={{ margin: 0 }}
            />
          )}
        </div>
      )}
      <div className="field" style={{ margin: 0 }}>
        <p className="hint">{offerHint(offer, salary)}</p>
      </div>
    </div>
  );
}
